/**
 * useChottuLink — initialise le SDK ChottuLink et gère la réception des deep links
 * Initialisation au niveau module (avant React) pour éviter la race condition
 * où onDeepLinkResolved tire avant que les listeners soient attachés.
 * src/hooks/useChottuLink.ts
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { ChottuLinkIonicSDK } from 'capacitor-chottulink-sdk';

const MOBILE_KEY = import.meta.env.VITE_CHOTTULINK_MOBILE_KEY as string;

// ─── Singleton — initialisé une seule fois au chargement du module ────────────
// Les handlers navigate sont enregistrés par les composants React.
const navigateHandlers: Array<(url: string) => void> = [];
// URL mise en buffer si onDeepLinkResolved tire avant que React soit monté
let pendingUrl: string | null = null;

if (Capacitor.isNativePlatform() && MOBILE_KEY && !MOBILE_KEY.startsWith('REPLACE_WITH')) {
  // 1. Listeners en premier — avant initialize() pour ne rien rater
  ChottuLinkIonicSDK.addListener('onDeepLinkResolved', ({ url }) => {
    if (navigateHandlers.length > 0) {
      navigateHandlers.forEach(fn => fn(url));
    } else {
      // React pas encore monté — on conserve l'URL pour la dispatcher au premier handler
      pendingUrl = url;
    }
  });

  ChottuLinkIonicSDK.addListener('onDeepLinkFailed', ({ error }) => {
    if (error !== 'No link found') {
      console.error('[ChottuLink] Deep link failed:', error);
    }
  });

  // 2. Initialisation après les listeners
  ChottuLinkIonicSDK.initialize({ apiKey: MOBILE_KEY }).catch(err => {
    console.error('[ChottuLink] Initialisation échouée:', err);
  });
}

// ─── Hook React — enregistre le handler navigate du composant courant ─────────
export const useChottuLink = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (url: string) => {
      try {
        const parsed = new URL(url);
        navigate(parsed.pathname + parsed.search, { replace: true });
      } catch {
        console.error('[ChottuLink] URL invalide:', url);
      }
    };

    navigateHandlers.push(handler);

    // Dispatcher l'URL mise en buffer si onDeepLinkResolved a tiré avant ce mount
    if (pendingUrl) {
      const url = pendingUrl;
      pendingUrl = null;
      handler(url);
    }

    return () => {
      const idx = navigateHandlers.indexOf(handler);
      if (idx >= 0) navigateHandlers.splice(idx, 1);
    };
  }, [navigate]);
};
